// Ruta /lobby — crear / unirse a una partida multiplayer (Sprint 10).
//
// El room ID es un slug corto que se muestra al jugador para compartir
// con un amigo. La conexión WebSocket se abre al entrar a /play/multi.

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '../components/ui/Button';
import { useT } from '../lib/i18n';

export const Route = createFileRoute('/lobby')({
  component: LobbyPage,
});

function generateRoomId(): string {
  // 4 caracteres alfanuméricos (≈ 1.6M combinaciones).
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 4; i += 1) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return id;
}

function LobbyPage() {
  const t = useT();
  const navigate = useNavigate();
  const [roomId, setRoomId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleCreate = (): void => {
    const id = generateRoomId();
    setError(null);
    void navigate({
      to: '/play/multi',
      search: { room: id, name: playerName || 'Anónimo' },
    });
  };

  const handleJoin = (): void => {
    const id = roomId.trim().toLowerCase();
    if (!id || id.length < 2) {
      setError('Ingresá un ID de sala válido (mínimo 2 caracteres).');
      return;
    }
    setError(null);
    void navigate({
      to: '/play/multi',
      search: { room: id, name: playerName || 'Anónimo' },
    });
  };

  return (
    <main id="main-content" className="min-h-screen bg-bg text-text">
      <div className="border-b border-border bg-bg-alt/40">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 md:px-6">
          <Link to="/" className="text-sm text-text-muted transition hover:text-gold">
            {t('common.back')}
          </Link>
          <h1 className="font-display text-lg text-gold">Multiplayer</h1>
          <span className="w-12" />
        </div>
      </div>

      <section className="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
        <div className="rounded-xl border border-border bg-bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">
            Tu nombre
          </h2>
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="Héroe"
            maxLength={24}
            className="w-full rounded-lg border border-border bg-bg-alt px-3 py-2 text-text placeholder:text-text-muted focus:border-gold focus:outline-none"
            data-testid="player-name-input"
          />
        </div>

        <div className="rounded-xl border border-border bg-bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">
            Crear partida
          </h2>
          <p className="mb-3 text-sm text-text-muted">
            Generamos un ID de sala aleatorio. Compartilo con un amigo.
          </p>
          <Button onClick={handleCreate} data-testid="create-room-btn">
            Crear partida
          </Button>
        </div>

        <div className="rounded-xl border border-border bg-bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">
            Unirse a partida
          </h2>
          <div className="flex gap-2">
            <input
              type="text"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              placeholder="ID de sala"
              maxLength={32}
              className="flex-1 rounded-lg border border-border bg-bg-alt px-3 py-2 text-text placeholder:text-text-muted focus:border-gold focus:outline-none"
              data-testid="room-id-input"
            />
            <Button onClick={handleJoin} data-testid="join-room-btn">
              Unirse
            </Button>
          </div>
          {error && (
            <p className="mt-2 text-sm text-blood" role="alert" data-testid="lobby-error">
              {error}
            </p>
          )}
        </div>
      </section>
    </main>
  );
}
